import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  DollarSign, User, Briefcase, Package, CreditCard, 
  CheckCircle, AlertTriangle, Send, Clock, History
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPayouts = () => {
  const { getAuthHeader } = useAuth();
  const [payoutData, setPayoutData] = useState([]);
  const [payoutHistory, setPayoutHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending');
  const [selectedPayout, setSelectedPayout] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [processingId, setProcessingId] = useState(null);
  const [externalPayIds, setExternalPayIds] = useState({});

  useEffect(() => {
    fetchPayoutData();
  }, []);

  const fetchPayoutData = async () => {
    setLoading(true);
    try {
      const [payoutsRes, historyRes] = await Promise.all([
        axios.get(`${API}/admin/payouts/pending`, getAuthHeader()),
        axios.get(`${API}/admin/payouts/history`, getAuthHeader())
      ]);
      setPayoutData(payoutsRes.data);
      setPayoutHistory(historyRes.data);
    } catch (error) {
      console.error('Error fetching payout data:', error);
      toast.error('Failed to fetch payout data');
    } finally {
      setLoading(false);
    }
  };

  const handleInitiatePayout = (payout) => {
    setSelectedPayout(payout);
    setConfirmDialog(true);
  };

  const handleConfirmPayout = async () => {
    if (!selectedPayout) return;
    
    setProcessingId(selectedPayout.user_id);
    setConfirmDialog(false);
    
    try {
      const response = await axios.post(
        `${API}/admin/payouts/send`,
        {
          user_id: selectedPayout.user_id,
          session_ids: selectedPayout.sessions.map(s => s.id),
          amount: selectedPayout.total_earnings
        },
        getAuthHeader()
      );
      
      toast.success(`Payout of $${selectedPayout.total_earnings.toFixed(2)} sent to ${selectedPayout.user_name}!`);
      fetchPayoutData();
    } catch (error) {
      console.error('Payout error:', error);
      toast.error(error.response?.data?.detail || 'Payout failed. Check PayPal credentials.');
    } finally {
      setProcessingId(null);
      setSelectedPayout(null);
    }
  };

  const handleMarkPaidExternal = async (payout) => {
    setExternalPayIds(prev => ({ ...prev, [payout.user_id]: true }));
    try {
      await axios.post(
        `${API}/admin/payouts/mark-paid-external`,
        {
          user_id: payout.user_id,
          session_ids: payout.sessions.map(s => s.id),
          amount: payout.total_earnings
        },
        getAuthHeader()
      );
      toast.success(`$${payout.total_earnings.toFixed(2)} marked as paid outside PayPal for ${payout.user_name}`);
      fetchPayoutData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark as paid');
    } finally {
      setExternalPayIds(prev => ({ ...prev, [payout.user_id]: false }));
    }
  };

  const pendingPayouts = payoutData.filter(p => p.status === 'pending');
  const completedPayouts = payoutHistory;

  const totalPendingAmount = pendingPayouts.reduce((sum, p) => sum + p.total_earnings, 0);
  const totalPaidAmount = completedPayouts.reduce((sum, p) => sum + p.amount, 0);

  return (
    <div className="p-6" data-testid="admin-payouts-page">
      <h1 className="text-2xl font-bold text-gray-900 mb-2">PayPal Payouts</h1>
      <p className="text-gray-500 mb-6">Process payments for completed deliveries</p>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card className="p-4 border-0 shadow-sm bg-gradient-to-br from-orange-50 to-orange-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-orange-500 rounded-lg flex items-center justify-center">
              <Clock size={20} className="text-white" />
            </div>
            <div>
              <p className="text-2xl font-bold text-orange-700">${totalPendingAmount.toFixed(2)}</p>
              <p className="text-sm text-orange-600">Pending Payouts</p>
            </div>
          </div>
        </Card>

        <Card className="p-4 border-0 shadow-sm bg-gradient-to-br from-green-50 to-green-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center">
              <CheckCircle size={20} className="text-white" />
            </div>
            <div>
              <p className="text-2xl font-bold text-green-700">${totalPaidAmount.toFixed(2)}</p>
              <p className="text-sm text-green-600">Total Paid Out</p>
            </div>
          </div>
        </Card>

        <Card className="p-4 border-0 shadow-sm bg-gradient-to-br from-blue-50 to-blue-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
              <User size={20} className="text-white" />
            </div>
            <div>
              <p className="text-2xl font-bold text-blue-700">{pendingPayouts.length}</p>
              <p className="text-sm text-blue-600">Users Awaiting Payment</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        <Button
          variant={activeTab === 'pending' ? 'default' : 'outline'}
          onClick={() => setActiveTab('pending')}
          className={activeTab === 'pending' ? 'bg-orange-600 hover:bg-orange-700' : ''}
          data-testid="pending-payouts-tab"
        >
          <DollarSign size={16} className="mr-2" />
          Pending Payouts ({pendingPayouts.length})
        </Button>
        <Button
          variant={activeTab === 'history' ? 'default' : 'outline'}
          onClick={() => setActiveTab('history')}
          className={activeTab === 'history' ? 'bg-green-600 hover:bg-green-700' : ''}
          data-testid="payout-history-tab"
        >
          <History size={16} className="mr-2" />
          Payout History ({completedPayouts.length})
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin w-8 h-8 border-4 border-orange-600 border-t-transparent rounded-full"></div>
        </div>
      ) : (
        <>
          {/* Pending Payouts */}
          {activeTab === 'pending' && (
            <div className="space-y-4" data-testid="pending-payouts-list">
              {pendingPayouts.length === 0 ? (
                <Card className="p-12 text-center border-0 shadow-sm">
                  <CheckCircle size={48} className="mx-auto text-green-300 mb-4" />
                  <p className="text-gray-500 text-lg">All caught up!</p>
                  <p className="text-gray-400 text-sm">No pending payouts at this time</p>
                </Card>
              ) : (
                pendingPayouts.map((payout) => (
                  <Card key={payout.user_id} className="p-5 border-0 shadow-sm" data-testid={`payout-card-${payout.user_id}`}>
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
                          <User size={24} className="text-gray-500" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900 text-lg">{payout.user_name}</h3>
                          <div className="flex items-center gap-2 text-sm text-blue-600">
                            <CreditCard size={14} />
                            <span>{payout.paypal_email}</span>
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-3xl font-bold text-green-600">${payout.total_earnings.toFixed(2)}</p>
                        <p className="text-xs text-gray-500">Total Owed</p>
                      </div>
                    </div>

                    {/* Job breakdown */}
                    <div className="bg-gray-50 rounded-lg p-4 mb-4">
                      <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                        <Briefcase size={14} />
                        Completed Jobs ({payout.sessions.length})
                      </h4>
                      <div className="space-y-2 max-h-40 overflow-y-auto">
                        {payout.sessions.map((session) => (
                          <div key={session.id} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                            <div className="flex items-center gap-3">
                              <Package size={14} className="text-orange-500" />
                              <div>
                                <p className="text-sm font-medium text-gray-800">
                                  {session.job_title}
                                  {session.created_at && (
                                    <span
                                      className="ml-2 text-xs font-normal text-blue-600"
                                      data-testid={`session-completed-date-${session.id}`}
                                      title="Date the worker completed this map"
                                    >
                                      Completed {new Date(session.created_at.endsWith?.('Z') ? session.created_at : session.created_at + 'Z').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                    </span>
                                  )}
                                </p>
                                <p className="text-xs text-gray-500">{session.deliveries_count} deliveries</p>
                              </div>
                            </div>
                            <span className="font-semibold text-green-600">${session.earnings.toFixed(2)}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Summary row */}
                    <div className="flex items-center justify-between text-sm text-gray-600 mb-4">
                      <span>Total Deliveries: <strong>{payout.total_deliveries}</strong></span>
                      <span>Avg per delivery: <strong>${payout.total_deliveries > 0 ? (payout.total_earnings / payout.total_deliveries).toFixed(3) : '0.00'}</strong></span>
                    </div>

                    <div className="flex gap-2">
                      <Button
                        className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-3"
                        onClick={() => handleInitiatePayout(payout)}
                        disabled={processingId === payout.user_id || externalPayIds[payout.user_id]}
                        data-testid={`send-payout-btn-${payout.user_id}`}
                      >
                        {processingId === payout.user_id ? (
                          <>
                            <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                            Processing PayPal Payout...
                          </>
                        ) : (
                          <>
                            <Send size={18} className="mr-2" />
                            Send ${payout.total_earnings.toFixed(2)} via PayPal
                          </>
                        )}
                      </Button>
                      <Button
                        variant="outline"
                        className="border-gray-300 text-gray-600 hover:bg-gray-50 py-3 whitespace-nowrap"
                        onClick={() => handleMarkPaidExternal(payout)}
                        disabled={processingId === payout.user_id || externalPayIds[payout.user_id]}
                        data-testid={`paid-external-btn-${payout.user_id}`}
                      >
                        {externalPayIds[payout.user_id] ? (
                          <div className="animate-spin w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full mr-2"></div>
                        ) : (
                          <CheckCircle size={16} className="mr-1" />
                        )}
                        Paid outside PayPal
                      </Button>
                    </div>
                  </Card>
                ))
              )}
            </div>
          )}

          {/* Payout History */}
          {activeTab === 'history' && (
            <div className="space-y-3" data-testid="payout-history-list">
              {completedPayouts.length === 0 ? (
                <Card className="p-12 text-center border-0 shadow-sm">
                  <History size={48} className="mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500">No payout history yet</p>
                </Card>
              ) : (
                completedPayouts.map((payout) => (
                  <Card key={payout.id} className="p-4 border-0 shadow-sm" data-testid={`payout-history-${payout.id}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                          <CheckCircle size={20} className="text-green-600" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900">{payout.user_name}</h3>
                          <p className="text-sm text-gray-500">{payout.paypal_email}</p>
                          <p className="text-xs text-gray-400">
                            {new Date(payout.created_at).toLocaleDateString()} at {new Date(payout.created_at).toLocaleTimeString()}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-xl font-bold text-green-600">${payout.amount.toFixed(2)}</p>
                        <Badge className={payout.payment_method === 'external' ? 'bg-gray-100 text-gray-700' : 'bg-green-100 text-green-700'}>
                          {payout.payment_method === 'external' ? 'Paid Externally' : 'PayPal'}
                        </Badge>
                      </div>
                    </div>
                    {payout.payout_batch_id && (
                      <p className="text-xs text-gray-400 mt-2">
                        PayPal Batch ID: {payout.payout_batch_id}
                      </p>
                    )}
                  </Card>
                ))
              )}
            </div>
          )}
        </>
      )}

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog} onOpenChange={setConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="text-orange-500" size={20} />
              Confirm PayPal Payout
            </DialogTitle>
            <DialogDescription>
              This action will send a LIVE PayPal payout. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          
          {selectedPayout && (
            <div className="py-4">
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-gray-600">Recipient:</span>
                  <span className="font-semibold">{selectedPayout.user_name}</span>
                </div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-gray-600">PayPal Email:</span>
                  <span className="font-semibold text-blue-600">{selectedPayout.paypal_email}</span>
                </div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-gray-600">Jobs Included:</span>
                  <span className="font-semibold">{selectedPayout.sessions.length}</span>
                </div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-gray-600">Total Deliveries:</span>
                  <span className="font-semibold">{selectedPayout.total_deliveries}</span>
                </div>
                <hr className="my-3" />
                <div className="flex justify-between items-center">
                  <span className="text-gray-800 font-medium">Payout Amount:</span>
                  <span className="text-2xl font-bold text-green-600">${selectedPayout.total_earnings.toFixed(2)}</span>
                </div>
              </div>
              
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800">
                <AlertTriangle size={16} className="inline mr-2" />
                This will initiate a real PayPal payout. Ensure the recipient's email is correct.
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDialog(false)}>
              Cancel
            </Button>
            <Button 
              className="bg-blue-600 hover:bg-blue-700"
              onClick={handleConfirmPayout}
              data-testid="confirm-payout-btn"
            >
              <Send size={16} className="mr-2" />
              Confirm & Send Payout
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPayouts;
